import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Logger from '@/utils/logger';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [copied, setCopied] = useState<'stack' | 'componentStack' | null>(null);
  const [showStack, setShowStack] = useState(true);
  const [showComponentStack, setShowComponentStack] = useState(true);

  Logger.error('Route error caught by ErrorBoundary:', error as Error);

  // Development-only detailed view
  if (import.meta.env.DEV) {
    let errorMessage = 'An unknown error occurred';
    let errorStack = '';
    let componentStack = '';

    if (error instanceof Error) {
      errorMessage = error.message;
      errorStack = error.stack || 'No stack trace available.';
      // @ts-expect-error componentStack is a non-standard property added by React
      componentStack = error.componentStack || '';
    } else if (isRouteErrorResponse(error)) {
      errorMessage = `${error.status} ${error.statusText}`;
      errorStack = error.data || 'No additional data.';
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    return (
      <div className="min-h-screen bg-background text-foreground p-4 sm:p-6 flex items-center justify-center">
        <div className="w-full max-w-4xl bg-card border border-destructive/20 rounded-lg shadow-lg p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <span className="text-xl font-bold text-destructive">!</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-destructive">Application Error</h1>
              <p className="text-sm text-muted-foreground">An error was caught by the boundary. See details below.</p>
            </div>
          </div>

          <div className="bg-muted/50 p-3 rounded-md">
            <p className="text-base font-semibold text-foreground break-words">{errorMessage}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between group">
              <button
                className="flex items-center gap-2 text-lg font-semibold transition-colors text-foreground group-hover:text-muted-foreground"
                onClick={() => setShowStack(!showStack)}
              >
                Stack Trace
                {showStack ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(errorStack);
                    setCopied('stack');
                    setTimeout(() => setCopied(null), 2000);
                  } catch (err) {
                    console.error('Failed to copy stack trace:', err);
                  }
                }}
              >
                {copied === 'stack' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            {showStack && (
              <pre className="bg-muted/50 p-3 rounded-md text-xs text-muted-foreground overflow-auto max-h-60 custom-scrollbar">
                <code>{errorStack}</code>
              </pre>
            )}
          </div>

          {componentStack && (
            <div className="space-y-2">
              <div className="flex items-center justify-between group">
                <button
                  className="flex items-center gap-2 text-lg font-semibold transition-colors text-foreground group-hover:text-muted-foreground"
                  onClick={() => setShowComponentStack(!showComponentStack)}
                >
                  Component Stack
                  {showComponentStack ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(componentStack);
                      setCopied('componentStack');
                      setTimeout(() => setCopied(null), 2000);
                    } catch (err) {
                      console.error('Failed to copy component stack:', err);
                    }
                  }}
                >
                  {copied === 'componentStack' ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              {showComponentStack && (
                <pre className="bg-muted/50 p-3 rounded-md text-xs text-muted-foreground overflow-auto max-h-60 custom-scrollbar">
                  <code>{componentStack}</code>
                </pre>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-border/50">
            <Button onClick={() => navigate(-1)} variant="outline">
              Go Back
            </Button>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
            <Button onClick={() => navigate('/', { replace: true })}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found';
      message = "The page you're looking for doesn't exist.";
    } else if (error.status === 401) {
      title = 'Unauthorized';
      message = 'You need to be logged in to access this page.';
    } else if (error.status === 403) {
      title = 'Forbidden';
      message = "You don't have permission to access this page.";
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="text-center space-y-4 w-full sm:max-w-md">
        <h1 className="font-semibold tracking-tight text-2xl sm:text-3xl">{title}</h1>
        <p className="text-muted-foreground">{message}</p>
        <div className="flex justify-center gap-4 pt-4">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
          >
            Go Back
          </Button>
          <Button
            onClick={() => navigate('/', { replace: true })}
          >
            Home
          </Button>
        </div>
      </div>
    </div>
  );
}
